import { H2 } from "@sellar-io/components";
import {
  OrderGetResponseType,
  OrderPaymentType,
} from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { OrderStage } from "../helpers";

export type OrderPaymentStatusProps = {
  paymentDue?: Date | null;
  paidAt?: Date | null;
  retailerPaidAt?: Date | null;
  paymentStatus: OrderGetResponseType["paymentStatus"];
  stripeInvoiceId?: string | null;
  confirmedAt: Date | null;
  paymentType: OrderPaymentType | null;
  refundedAmount: number;
  paidAmount: number;
  price: number;
  PaymentList: {
    type: "cardDeposit" | "sellarCredit" | null;
    status: "paid" | "refunded" | "void" | "pending" | "overdue" | "failed";
    amount: number;
  }[];
};

export const OrderPaymentStatus = ({
  order,
  orderStage,
  isImplicitProforma = false,
}: {
  order: OrderPaymentStatusProps;
  orderStage: OrderStage;
  isImplicitProforma?: boolean;
}): ReactElement | null => {
  const { business } = useSellarCurrentBusiness();
  const isBuyer = !!business?.Retailer;
  const isSupplier = !!business?.Supplier;
  const orderPaymentType = order.paymentType;
  // Capture "now" once so the status is stable for this mounted order.
  const [now] = useState(Date.now);

  if (order.price === 0) {
    return <H2 className={"text-gray-500"}>No payment due</H2>;
  }

  if (order.paymentStatus === "refunded") {
    return <H2 className={"text-gray-500"}>Refunded</H2>;
  }

  if (orderStage === "Cancelled") {
    return (
      <H2 className={"text-gray-500"}>{order.paidAt ? "Voided" : "Unpaid"}</H2>
    );
  }

  if (orderStage === "Voided") {
    return <H2 className="text-gray-500">Voided</H2>;
  }

  if (order.refundedAmount) {
    return <H2 className={"text-gray-500"}>Partially refunded</H2>;
  }

  if (order.paidAmount > order.price && !order.confirmedAt) {
    return <H2 className={"text-gray-500"}>Refund due</H2>;
  }

  const showPaidBuyer = isBuyer && !!order.retailerPaidAt;
  const showPaidSupplier = isSupplier && !!order.paidAt;
  if (showPaidBuyer || showPaidSupplier) {
    return <H2 className={"text-gray-500"}>Paid</H2>;
  }

  if (orderPaymentType === "cardPaymentWithDeposit") {
    const orderDepositPaid = order.PaymentList.find(
      (payment) => payment.status === "paid" && payment.type === "cardDeposit",
    );

    return (
      <H2 className={"text-gray-500"}>
        Deposit {orderDepositPaid ? "paid" : "unpaid"}
      </H2>
    );
  }

  if (order.paymentType === "checkout") {
    return <H2 className={"text-gray-500"}>Due today</H2>;
  }

  if (isImplicitProforma) {
    return null;
  }

  if (
    !order.paymentDue ||
    (order.paymentType === "stripe" && order.paymentStatus === "draft")
  ) {
    return <H2 className={"text-gray-500"}>Unpaid</H2>;
  }

  const paymentdueDate = order.paymentDue;

  if (dayjs(paymentdueDate).isSame(now, "day")) {
    return <H2 className={"text-gray-500"}>Due today</H2>;
  }

  if (dayjs(paymentdueDate).isAfter(now, "day")) {
    const diff = dayjs(paymentdueDate).diff(now, "day");
    return (
      <H2 className={"text-gray-500"}>
        Due {diff < 1 ? "tomorrow" : dayjs(paymentdueDate).fromNow()}
      </H2>
    );
  }

  if (dayjs(paymentdueDate).isBefore(now, "day")) {
    return (
      <H2 className={"text-gray-500"}>
        Due {dayjs(now).diff(paymentdueDate, "days")} days ago
      </H2>
    );
  }

  return null;
};
